<template>
  <div class="vehicle-models-mega-menu p-6">
    <!-- Loading State -->
    <div v-if="modelsStore.loading" class="flex justify-center py-8">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
    
    <!-- Error State -->
    <div v-else-if="modelsStore.error" class="py-8 text-center">
      <p class="text-destructive">{{ modelsStore.error }}</p>
    </div>
    
    <!-- Content -->
    <div v-else class="flex gap-12">
      <!-- Left Sidebar: Categories -->
      <div class="w-80 max-w-[400px] flex flex-col flex-shrink-0">
        <h2 class="text-sm font-semibold text-muted-foreground tracking-wider mb-4">MODELS</h2>
        <nav class="flex flex-col space-y-3">
          <button 
            v-for="category in modelsStore.uniqueCategories" 
            :key="category"
            class="text-left text-lg font-medium transition-colors"
            :class="activeCategory === category ? 'text-primary underline underline-offset-4' : 'text-foreground hover:text-primary'"
            @click="activeCategory = category"
          >
            {{ category }}
          </button>
        </nav>
        <NuxtLink 
          to="/models" 
          class="flex items-center text-sm text-foreground/80 hover:text-foreground mt-8"
          @click="requestClose"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
          Models Overview
        </NuxtLink>
      </div>

      <!-- Right Content: Models Grid (Hyundai Style) -->
      <div 
        ref="rightContentRef" 
        class="flex-1 pr-2 h-full overflow-y-auto"
        :style="rightPanelStyle"
      > 
        <!-- Empty State -->
        <div v-if="filteredModels.length === 0" class="py-8 text-center">
          <p class="text-muted-foreground">Select a category to view models.</p>
        </div>
        
        <!-- More fluid grid layout (using auto-fill) -->
        <div v-else class="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-x-6 gap-y-8">
          <!-- Loop through models -->
          <div 
            v-for="model in filteredModels" 
            :key="model.id"
            class="flex flex-col max-w-[450px]"
          >
            <!-- Image Area -->
            <div class="relative bg-gray-100 p-4 mb-4"> 
               <!-- Badge (Uses model vehiclecat) -->
               <div class="absolute top-3 left-3 flex items-center bg-background/80 backdrop-blur-sm px-2 py-0.5 rounded text-xs z-10"> 
                 <span class="w-2 h-2 bg-blue-500 rounded-full mr-1.5"></span>
                 <span>{{ model.vehiclecat || model.title.rendered }}</span>
               </div>
               <!-- Image Link -->
               <NuxtLink :to="`/kia/${model.slug}`" @click="requestClose" class="block aspect-[16/9]">
                 <img 
                    :src="model.model_image" 
                    :alt="model.title.rendered"
                    class="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
                  />
               </NuxtLink>
            </div>

            <!-- Text Content Area -->
            <div class="flex flex-col flex-grow px-2 pb-2">
              <h3 class="text-xl font-medium mb-2">{{ model.title.rendered }}</h3>
              <!-- Description using model.caption -->
              <p 
                v-if="model.caption" 
                class="text-sm text-muted-foreground mb-4 flex-grow"
              >
                {{ model.caption }}
              </p>
              <!-- Explore Link -->
              <div class="mt-auto flex justify-between">
                <NuxtLink 
                  :to="`/kia/${model.slug}`"
                  class="font-semibold text-primary hover:underline underline-offset-2 inline-flex items-center text-sm group"
                  @click="requestClose"
                >
                  Explore <span class="ml-1 transition-transform group-hover:translate-x-1">→</span>
                </NuxtLink>

                <NuxtLink 
                  :to="`/build/${model.slug}`"
                  class="font-semibold text-primary hover:underline underline-offset-2 inline-flex items-center text-sm group"
                  @click="requestClose"
                >
                  Build <span class="ml-1 transition-transform group-hover:translate-x-1">→</span>
                </NuxtLink>

                <NuxtLink 
                  :to="`/kia/models/${model.slug}`"
                  class="font-semibold text-primary hover:underline underline-offset-2 inline-flex items-center text-sm group"
                  @click="requestClose"
                >
                  Range <span class="ml-1 transition-transform group-hover:translate-x-1">→</span>
                </NuxtLink>

              </div>
            </div>
          </div>
          <!-- End v-for loop -->
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { useModelsStore } from '~/stores/models'
import { useScroll, useWindowSize } from '@vueuse/core'

// Define the event that will be emitted
const emit = defineEmits(['close-request', 'category-activated'])

const modelsStore = useModelsStore()
const activeCategory = ref('')

// Template ref for the right content scrollable container
const rightContentRef = ref<HTMLElement | null>(null)

// Initialize useScroll for the right content container
const { y: rightContentScrollY } = useScroll(rightContentRef)

// Get window size for dynamic height calculation
const { height: windowHeight } = useWindowSize()

// Calculate dynamic max-height for the right panel
const rightPanelStyle = computed(() => {
  // Mega menu has p-6 (1.5rem = 24px top/bottom padding)
  const megaMenuVerticalPadding = 24 * 2 // Top and bottom
  const bottomBuffer = 50 // Desired space from bottom of viewport
  
  // Ensure calculated height is not negative
  const calculatedMaxHeight = windowHeight.value - megaMenuVerticalPadding - bottomBuffer
  const maxHeight = Math.max(100, calculatedMaxHeight) // Ensure a minimum height (e.g., 100px)
  
  return {
    maxHeight: `${maxHeight}px`
  }
})

// Set initial active category (Keep SUV for now, or change if needed)
watch(() => modelsStore.uniqueCategories, (categories) => {
  if (categories.length > 0 && !activeCategory.value) {
    const initialCategory = categories.find(c => c.toLowerCase() === 'performance') || categories[0]; // Set to 'performance' based on image
    if (initialCategory) {
      activeCategory.value = initialCategory;
    }
  }
}, { immediate: true })

// Filter models by active category - using vehiclecat instead of assuming segment_desc
const filteredModels = computed(() => {
  if (!activeCategory.value) return []
  
  // Return models filtered by vehiclecat (category)
  return modelsStore.modelsByCategory(activeCategory.value)
})

// Watch for activeCategory changes to scroll the right content panel to the top
watch(activeCategory, async () => {
  await nextTick() // Ensure the DOM has updated with the new set of filteredModels
  rightContentScrollY.value = 0 // Scroll the right content area to the top
  emit('category-activated') // Still emit, in case parent needs it for other reasons
})

// Ensure models are loaded when this menu is used
onMounted(async () => {
  if (!modelsStore.isInitialized && !modelsStore.loading) {
    try {
      await modelsStore.fetchModels()
    } catch (err) {
      // Error state will be reflected via modelsStore.error
    }
  }
})

// Function to emit the close request event
const requestClose = () => {
  emit('close-request')
}
</script>

<style scoped>
/* Re-enabled scrollbar styles */
.max-h-\[550px\]::-webkit-scrollbar {
  width: 6px;
}

.max-h-\[550px\]::-webkit-scrollbar-track {
  background: transparent;
}

.max-h-\[550px\]::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.5); /* gray-400/50 */
  border-radius: 3px;
}

.max-h-\[550px\]::-webkit-scrollbar-thumb:hover {
  background-color: rgba(107, 114, 128, 0.5); /* gray-500/50 */
}
</style>
